import { useMutation } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { ssoApi } from "@repo/api";

export interface SsoTestResult {
  success: boolean;
  message?: string;
  timestamp: number;
}

interface UseTestConnectionReturn {
  testConnection: () => void;
  isLoading: boolean;
  error: string | null;
  testResult: SsoTestResult | null;
  clearResult: () => void;
  isBroadcastSupported: boolean;
}

const BROADCAST_CHANNEL_NAME = "sso-test-result";
const BROADCAST_MESSAGE_TYPE = "SSO_TEST_RESULT";
const SSO_TEST_PENDING_KEY = "sso-test-pending";

export const useTestConnection = (): UseTestConnectionReturn => {
  const intl = useIntl();
  const [error, setError] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<SsoTestResult | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const popupBlockedRef = useRef<boolean>(false);
  const isBroadcastSupported = typeof BroadcastChannel !== "undefined";

  // Set up BroadcastChannel listener on mount
  useEffect(() => {
    if (!isBroadcastSupported) {
      return;
    }

    const channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
    channelRef.current = channel;

    channel.onmessage = (event: MessageEvent) => {
      // Do not process BroadcastChannel messages if popup was blocked
      if (popupBlockedRef.current) {
        return;
      }

      const data = event.data;
      if (data && data.type === BROADCAST_MESSAGE_TYPE) {
        const payload = data.payload as SsoTestResult;
        setTestResult(payload);
        setError(null);
      }
    };

    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [isBroadcastSupported]);

  // Clean up sso-test-pending flag when navigating away from SSO settings page.
  // This prevents stale flags from interfering with normal SSO login flows.
  useEffect(() => {
    return () => {
      localStorage.removeItem(SSO_TEST_PENDING_KEY);
    };
  }, []);

  const mutation = useMutation({
    mutationFn: () => ssoApi.testConnection(),
    onSuccess: (data) => {
      const { redirectUrl } = data;

      // Set localStorage flag with timestamp so the callback page knows this is a test
      localStorage.setItem(SSO_TEST_PENDING_KEY, Date.now().toString());

      const newWindow = window.open(redirectUrl, "_blank");

      if (!newWindow) {
        // Popup blocked — clear the flag and stop listening for BroadcastChannel results
        popupBlockedRef.current = true;
        localStorage.removeItem(SSO_TEST_PENDING_KEY);
        setError(
          intl.formatMessage({
            id: "wQFbjb",
            defaultMessage:
              "Please allow popups for this site to test the connection.",
            description: "Error when browser blocks the SSO test popup window",
          }),
        );
        return;
      }

      // Popup opened successfully — ensure BroadcastChannel listener is active
      popupBlockedRef.current = false;
    },
    onError: (err: unknown) => {
      // Handle specific HTTP status codes
      if (
        err instanceof Error &&
        "status" in err &&
        (err as { status: number }).status === 400
      ) {
        setError(
          intl.formatMessage({
            id: "wmNCdV",
            defaultMessage:
              "SSO is not configured. Please save your configuration first.",
            description:
              "Error when test connection is attempted before SSO is configured",
          }),
        );
        return;
      }

      // Network failure or other errors
      setError(
        intl.formatMessage({
          id: "djfymb",
          defaultMessage:
            "Unable to reach the server. Please check your connection.",
          description:
            "Error when network request fails during SSO test connection",
        }),
      );
    },
  });

  const testConnection = useCallback(() => {
    setError(null);
    setTestResult(null);
    // Reset popup blocked state for new test attempt
    popupBlockedRef.current = false;
    mutation.mutate();
  }, [mutation]);

  const clearResult = useCallback(() => {
    setTestResult(null);
    setError(null);
  }, []);

  return {
    testConnection,
    isLoading: mutation.isPending,
    error,
    testResult,
    clearResult,
    isBroadcastSupported,
  };
};
